import type { ReactNode } from 'react';
import {
  usePracticeUsers,
  useTasksFromProjectId,
} from '@witmetrics/data-store';
import {
  filterUnisonTasks,
  getTaskFilterCount,
} from '@witmetrics/utils/filters';
import { mapToKey } from '@witmetrics/utils/arrays';
import { isEmpty } from '@witmetrics/utils/misc';
import { useAppState } from '@/providers/AppStateProvider';
import { useToggle } from '@/hooks/useToggle';
import { useIds } from '@/hooks/useIds';
import { Tab } from '@/components/TabsBlock';
import Section from '@/overlays/CaseOverlay/Section';
import SectionTitle from '@/overlays/CaseOverlay/SectionTitle';
import UnisonProjectTasksList from '@/components/UnisonProjectTasksList';
import UnisonProjectTaskDialog from '@/dialogs/UnisonProjectTaskDialog';
import TasksFilterMenu from '@/menus/TasksFilterMenu';
import EmptyMessage from '@/overlays/CaseOverlay/EmptyMessage';
import StatusToggle from './StatusToggle';
import { classes } from './utils';

export type TasksTabProps = {
  isFetchingData: boolean;
  name: string;
  label: string;
  adornment?: ReactNode;
};

export default function TasksTab({
  isFetchingData,
  name,
  label,
  adornment,
}: TasksTabProps) {
  const { activeProjectId } = useAppState();
  const practiceUsers = usePracticeUsers();
  const tasks = useTasksFromProjectId(activeProjectId);
  const [userIds, setUserIds] = useIds(mapToKey(practiceUsers, 'userId'));
  const [showAll, toggleShowAll] = useToggle();
  const [isTaskDialogShown, toggleTaskDialog] = useToggle();

  const getFiltersCount = () => {
    return getTaskFilterCount([], userIds, practiceUsers);
  };

  const renderTaskDialog = () => {
    if (!isTaskDialogShown) return null;
    return (
      <UnisonProjectTaskDialog
        projectId={activeProjectId!}
        onClose={() => toggleTaskDialog(false)}
      />
    );
  };

  const renderTasks = () => {
    if (!isFetchingData && isEmpty(tasks)) {
      return <EmptyMessage>No tasks for current case.</EmptyMessage>;
    }
    let filteredTasks = filterUnisonTasks(tasks, [], userIds);
    if (!showAll) {
      // Only show unresolved tasks
      filteredTasks = filteredTasks.filter(({ task }) => !task.isResolved);
    }
    if (!isFetchingData && isEmpty(filteredTasks)) {
      return <EmptyMessage>No tasks matching current filters.</EmptyMessage>;
    }
    return (
      <UnisonProjectTasksList
        isFetchingData={isFetchingData}
        className={classes.list}
        unisonProjectTasks={filteredTasks}
      />
    );
  };

  if (!activeProjectId) return null;

  return (
    <Tab
      className={classes.tab}
      name={name}
      label={label}
      adornment={adornment}>
      {renderTaskDialog()}
      <Section>
        <div className={classes.card}>
          <div className={classes.cardHeader}>
            <SectionTitle
              title="Tasks"
              newLabel="New task"
              filtersCount={getFiltersCount()}
              onAddNew={() => toggleTaskDialog(true)}
              renderControls={() => (
                <StatusToggle
                  className={classes.toggle}
                  optionClassName={classes.toggleOption}
                  value={showAll}
                  onChange={toggleShowAll}
                />
              )}
              renderFilterMenu={(props) => (
                <TasksFilterMenu
                  userFilters={userIds}
                  onApplyFilters={(update) => setUserIds(update.userFilters)}
                  {...props}
                />
              )}
            />
          </div>
          {renderTasks()}
        </div>
      </Section>
    </Tab>
  );
}
